import { captureException, withErrorBoundary } from '@sentry/react'
import { type QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRootRouteWithContext, notFound, Outlet, redirect } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'

import { type FirebaseUserContextValue } from '@/auth/FirebaseUserContextValue'
import { SessionGuard } from '@/auth/sessionGuard/SessionGuard'
import { SupportHeaderSlotContext } from '@/components/organisms/SupportButton'
import { SentryErrorPage } from '@/pages/error/SentryErrorPage'
import { isSupportEmbed } from '@/pages/support/embed/piloteFrame'
import { SupportAgentAlerts } from '@/pages/support/SupportAgentAlerts'
import { SupportEntry } from '@/pages/support/SupportEntry'

const SUPPORT_PATH = '/soutien'

const supportEntry = <SupportEntry />

const DevRootRoute = () => (
  <SupportHeaderSlotContext value={supportEntry}>
    <Outlet />
    <SessionGuard />
    <SupportAgentAlerts />
    {!isSupportEmbed() && (
      <>
        <TanStackRouterDevtools />
        <ReactQueryDevtools initialIsOpen={false} />
      </>
    )}
  </SupportHeaderSlotContext>
)

export const Route = createRootRouteWithContext<{
  readonly queryClient: QueryClient
  readonly auth: FirebaseUserContextValue
}>()({
  beforeLoad: ({ context: { auth }, location }) => {
    if (!auth.supportOnly || location.pathname.startsWith(SUPPORT_PATH)) {
      return
    }
    if (location.pathname === '/') {
      throw redirect({ to: SUPPORT_PATH, replace: true })
    }
    globalThis.location.href = import.meta.env.VITE_PILOTE_BASE_URL
    throw notFound()
  },
  component: withErrorBoundary(DevRootRoute, {
    fallback: SentryErrorPage,
    beforeCapture: (scope) => scope.setTag('route', Route.fullPath),
  }),
  onCatch: (error) => {
    captureException(error, { tags: { route: Route.fullPath } })
  },
})
